import { useEffect, useRef, useState } from 'react';

export default function WhyUs() {
  const [isVisible, setIsVisible] = useState(false);
  const [partnersVisible, setPartnersVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const partnersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === sectionRef.current) setIsVisible(true);
            if (entry.target === partnersRef.current) setPartnersVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (partnersRef.current) observer.observe(partnersRef.current);

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: 'ri-home-heart-line',
      title: 'العناية الشخصية',
      description: 'نوفر لك خدمة شخصية متميزة في راحة منزلك مع الحفاظ على خصوصيتك الكاملة'
    },
    {
      icon: 'ri-user-star-line',
      title: 'أخصائيون سعوديون',
      description: 'فريق من الأخصائيين السعوديين المؤهلين ذوي خبرة وكفاءة في المجال'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'أسعار تنافسية',
      description: 'نقدم أفضل الأسعار مع جودة عالية في الخدمة والمتابعة المستمرة'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'دعم مستمر',
      description: 'متابعة دورية وخدمة عملاء متاحة على مدار الساعة لضمان راحتك'
    }
  ];

  const partners = [
    { name: 'Hansaton', logo: 'https://static.readdy.ai/image/8f4b99b6f50522f57c0c4089909ec764/cf4c6f994e89b82abd4f11eb93f8abb7.png' },
    { name: 'Widex', logo: 'https://static.readdy.ai/image/8f4b99b6f50522f57c0c4089909ec764/c06b0014fb32b779009bc890839f87ec.png' },
    { name: 'Starkey', logo: 'https://static.readdy.ai/image/8f4b99b6f50522f57c0c4089909ec764/31cbc4649a28b083f7ac9c12dc3b36c0.png' },
    { name: 'Helix', logo: 'https://static.readdy.ai/image/8f4b99b6f50522f57c0c4089909ec764/1676ca9754017d841b06bf6fbe177dd2.png' },
    { name: 'SONIC', logo: 'https://static.readdy.ai/image/8f4b99b6f50522f57c0c4089909ec764/c5ef5ccb226bff23f576b1ab68691f84.png' },
    { name: 'Bernafon', logo: 'https://static.readdy.ai/image/8f4b99b6f50522f57c0c4089909ec764/57af1132be0d553c465ae07ffbbd6561.png' },
  ];

  return (
    <>
      {/* Why Us Section */}
      <section id="why-us" ref={sectionRef} className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4" style={{ maxWidth: '1140px' }}>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div
              className={`transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}
            >
              <div className="inline-block bg-[#184187]/10 px-5 py-2 rounded-full mb-4">
                <span className="text-[#184187] font-semibold text-sm">لماذا سماعتي؟</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                لماذا تختارنا؟
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                هدفنا: العناية الشخصية، ضمان الخصوصية، وتحسين جودة حياتك، خصوصاً لمن يصعب عليهم زيارة العيادات.
              </p>
              
              <div className="space-y-6">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-4 bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-500 hover:-translate-x-1 ${
                      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                    }`}
                    style={{ transitionDelay: `${(index + 1) * 150}ms` }}
                  >
                    <div className="w-12 h-12 bg-[#184187] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${feature.icon} text-2xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              className={`transition-all duration-1000 delay-300 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-[#184187]/10 rounded-2xl transform rotate-3 transition-transform duration-500 group-hover:rotate-6"></div>
                <img
                  src="https://public.readdy.ai/ai/img_res/c7b89422ff10bc532a4f0ff2c5f5f1a7.jpg"
                  alt="لماذا تختار سماعتي"
                  className="relative rounded-2xl shadow-2xl w-full h-[600px] object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners / Hearing Aid Brands */}
      <section ref={partnersRef} className="py-16 bg-gray-50">
        <div className="container mx-auto px-4" style={{ maxWidth: '1140px' }}>
          <div
            className={`text-center mb-12 transition-all duration-1000 ${
              partnersVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="inline-block bg-[#184187]/10 px-5 py-2 rounded-full mb-4">
              <span className="text-[#184187] font-semibold text-sm">شركاؤنا</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              ماركات السماعات المعتمدة
            </h2>
            <p className="text-lg text-gray-600">
              نتعامل مع أفضل العلامات التجارية العالمية في صناعة السماعات الطبية
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {partners.map((partner, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl p-5 flex items-center justify-center shadow-sm hover:shadow-lg transition-all duration-500 hover:-translate-y-1 group cursor-default ${
                  partnersVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-center">
                  <div className="w-full h-16 flex items-center justify-center mb-3 grayscale group-hover:grayscale-0 transition-all duration-500 opacity-70 group-hover:opacity-100">
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <span className="text-sm font-semibold text-gray-600 group-hover:text-[#184187] transition-colors duration-300">{partner.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
